import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useFixedAppStyles } from './useAppStyles';
import { useTheme } from './ThemeContext';
import SummaryScreen from './SummaryScreen';
import { getRandomCongrats, getRandomProverb, getSentierName } from '../data/summaryData';
import { getSentenceTranslation } from '../utils/localizedContent';
import FloatingBackButton from './FloatingBackButton';
import TribalBorder from './TribalBorder';
import { 
  ArrowLeft,
  Volume2,
  RotateCw,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Home,
  Book,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { 
  LanguageCode,
  SentenceConstructionGameProps,
  Sentence,
  UserProgress,
  LearnedSentence
} from '../types/types';
import { getLanguageData } from '../data/languages';
import { useSupabaseAudio } from '../hooks/useSupabaseAudio'; // ✅ Nouveau système
import useSentenceRevision, { type SentenceRevisionInfo } from '../hooks/useSentenceRevision';
import SentenceRevisionGame from './SentenceRevisionGame';
import ConfirmationModal from './ConfirmationModal';
import XPAnimation from './XPAnimation';

import './SentenceConstructionGame.css';
import './LearnMode.css';
import { useSummaryAudio } from '../utils/summaryAudio';
import { EmojiText, Picto } from './common/Picto';
const SENTENCES_PER_LESSON = 5;
const ItemTypes = { WORD: "word" };




// ✅ SYSTÈME DE SAUVEGARDE OPTIMISÉ
interface OptimizedSessionData {
  id: string;
  startTime: number;
  endTime?: number;
  language: LanguageCode;
  category?: string;
  sentences: LearnedSentence[];
  stats: {
    score: number;
    totalItems: number;
    accuracy: number;
    duration: number;
    xpGained: number;
  };
}


class SimpleSaveManager {
  private currentSession: OptimizedSessionData | null = null;
  private saveTimeout: NodeJS.Timeout | null = null;
  private readonly SAVE_DELAY = 5000;

  startSession(language: LanguageCode, category?: string): string {
    const sessionId = `sentence-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    this.currentSession = {
      id: sessionId,
      startTime: Date.now(),
      language,
      category,
      sentences: [],
      stats: {
        score: 0,
        totalItems: 0,
        accuracy: 0,
        duration: 0,
        xpGained: 0
      }
    };
    
  
    return sessionId;
  }

  addSentence(sentence: LearnedSentence, score: number, totalItems: number) {
    if (!this.currentSession) return;

    this.currentSession.sentences.push(sentence);
    this.currentSession.stats.score = score;
    this.currentSession.stats.totalItems = totalItems;
    this.currentSession.stats.accuracy = totalItems > 0 ? (score / totalItems) * 100 : 0;

    localStorage.setItem(
      `${this.currentSession.language}-temp-session`, 
      JSON.stringify(this.currentSession)
    );
  }

  async finishSession(): Promise<{ xpGained: number; sessionData: OptimizedSessionData } | null> {
    if (!this.currentSession) return null;

    this.currentSession.endTime = Date.now();
    this.currentSession.stats.duration = this.currentSession.endTime - this.currentSession.startTime;
    
    // ✅ CORRECTION: Calculer l'XP seulement pour les phrases MAÎTRISÉES
    const masteredSentences = this.currentSession.sentences.filter(s => s.mastered);
    const xpGained = masteredSentences.length * 15; // Au lieu de this.currentSession.sentences.length * 15
    this.currentSession.stats.xpGained = xpGained;

  

    const result = {
      xpGained,
      sessionData: { ...this.currentSession }
    };

    this.currentSession = null;
    return result;
  }

  clearTimers(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
   
    }
  }

  hasActiveTimers(): boolean {
    return this.saveTimeout !== null;
  }

  hasRealDataToSave(): boolean {
    if (this.currentSession && this.currentSession.sentences.length > 0) {
      return true;
    }


    
    
    const tempKeys = Object.keys(localStorage).filter(key => key.endsWith('-temp-session'));
    return tempKeys.some(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        return data.sentences && Array.isArray(data.sentences) && data.sentences.length > 0;
      } catch {
        return false;
      }
    });
  }

  reset(): void {
 
    this.currentSession = null;
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }
}


// ✅ NOUVELLE FONCTION UTILITAIRE POUR AUDIO (identique à GrammarMode)
const hasValidAudio = (audio: string | undefined): boolean => {
  return !!audio && audio.trim() !== '';
};

const saveManager = new SimpleSaveManager();


const useSentenceXP = (languageCode: LanguageCode, category?: string) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showXPAnimation, setShowXPAnimation] = useState(false);
  const [xpGained, setXpGained] = useState(0);

  const startSession = useCallback(() => {
    const id = saveManager.startSession(languageCode, category);
    setSessionId(id);
    return id;
  }, [languageCode, category]);

  const addSentence = useCallback((sentence: LearnedSentence, score: number, totalItems: number) => {
    if (sessionId) {
      saveManager.addSentence(sentence, score, totalItems);
    }
  }, [sessionId]);

  const finishSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      const result = await saveManager.finishSession();
      
      if (result && result.xpGained > 0) {
        setXpGained(result.xpGained);
        // ✅ Ne pas déclencher l'animation automatiquement ici
      
      }
      
      setSessionId(null);
      
    } catch (error) {
}
  }, [sessionId]);

 
  const triggerXPAnimation = useCallback(() => {
    
    if (xpGained > 0) {
      setShowXPAnimation(true);
    }
  }, [xpGained]);

  const handleXPAnimationComplete = useCallback(() => {

    setShowXPAnimation(false);
    // ✅ CORRECTION: Ne plus remettre xpGained à zéro pour garder l'affichage
    // setXpGained(0); - SUPPRIMÉ
  }, []);

  return {
    startSession,
    addSentence,
    finishSession,
    sessionId,
    triggerXPAnimation, // ✅ CORRECTION: Exposer la fonction
    xpAnimationProps: {
      xpGained,
      showAnimation: showXPAnimation,
      onAnimationComplete: handleXPAnimationComplete,
      variant: 'large' as const,
      className: 'session-complete-xp'
    }
  };
};

// ✅ INTERFACES DND
interface WordWithId {
  id: string;
  text: string;
  index: number;
  // Kama only: the hieroglyph glyph to render instead of `text` (which stays
  // the transliteration — handleAnswer scores against `.text`, never this).
  displayText?: string;
}

interface FrenchWordWithId {
  id: string;
  text: string;
  index: number;
  isCorrect?: boolean;
}

interface TranslationState {
  wolofSentence: string;
  correctFrenchWords: string[];
  availableFrenchWords: FrenchWordWithId[];
  constructedTranslation: FrenchWordWithId[];
  distractorWords: string[];
}

interface DraggableWordProps {
  word: WordWithId;
  index: number;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  onWordClick?: (word: WordWithId) => void;
}

const DraggableWord: React.FC<DraggableWordProps> = ({ word, index, moveWord, isCorrect, onWordClick }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.WORD,
    item: { type: ItemTypes.WORD, id: word.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.WORD,
    hover(item: { index: number }, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      moveWord(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`modern-word-item ${isDragging ? 'dragging' : ''} ${isCorrect ? 'success' : ''} constructed-word ${word.displayText ? 'constructed-word--hieroglyph' : ''}`}
      style={{ cursor: 'pointer' }}
      onClick={() => onWordClick?.(word)}
    >
      {word.displayText ?? word.text}
      {word.displayText && (
        <span className="constructed-word__caption">{word.text}</span>
      )}
    </div>
  );
};

const DraggableFrenchWord: React.FC<{
  word: FrenchWordWithId;
  index: number;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  source: 'available' | 'constructed';
  onWordClick?: (word: FrenchWordWithId) => void;
}> = ({ word, index, moveWord, isCorrect, source, onWordClick }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.WORD,
    item: { type: ItemTypes.WORD, id: word.id, index, source, word },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.WORD,
    hover(item: { index: number; source: string }, monitor) {
      if (!ref.current || source !== 'constructed' || item.source !== 'constructed') return;
      
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      moveWord(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  if (source === 'constructed') {
    drag(drop(ref));
  } else {
    drag(ref);
  }

  return (
    <div
      ref={ref}
      className={`modern-word-item ${isDragging ? 'dragging' : ''} ${isCorrect ? 'success' : ''} ${source === 'available' ? 'available-french-word' : 'constructed-french-word'}`}
      onClick={() => onWordClick?.(word)}
      style={{ cursor: 'pointer' }}
    >
      {word.text}
    </div>
  );
};

const TranslationDropZone: React.FC<{
  constructedWords: FrenchWordWithId[];
  onWordDrop: (word: FrenchWordWithId) => void;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  onWordClick?: (word: FrenchWordWithId) => void;
}> = ({ constructedWords, onWordDrop, moveWord, isCorrect, onWordClick }) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.WORD,
    drop: (item: { id: string; source: string; word?: FrenchWordWithId }) => {
      if (item.source === 'available' && item.word) {
        onWordDrop(item.word);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`modern-construction-area translation-drop-zone ${isOver ? 'drop-over' : ''} ${isCorrect ? 'success' : ''}`}
    >
      {constructedWords.length === 0 ? (
        <div className="drop-zone-placeholder">
          {t('sentenceConstruction.dropPlaceholder')}
        </div>
      ) : (
        constructedWords.map((word, index) => (
          <DraggableFrenchWord
            key={word.id}
            word={word}
            index={index}
            moveWord={moveWord}
            isCorrect={isCorrect}
            source="constructed"
            onWordClick={onWordClick}
          />
        ))
      )}
    </div>
  );
};

// Encart info grammaticale — toutes les langues
const GRAMMAR_TIPS: Record<string, { label: string; formula: string; example: string }[]> = {
  ba: [
    { label: 'Présent', formula: 'Sujet + bɛ + Objet + Verbe', example: 'N bɛ dumuni kɛ — Je fais la cuisine' },
    { label: 'Passé (ye)', formula: 'Sujet + ye + Objet + Verbe', example: 'N ye a jɛ — Je l\'ai vu(e)' },
    { label: 'Passé (ra/la/na)', formula: 'Verbe + ra / la / na', example: 'A taara — Il/Elle est parti(e)' },
    { label: 'Futur', formula: 'Sujet + bɛna + Verbe', example: 'N bɛna taa — J\'irai' },
    { label: 'Attributif', formula: 'Sujet + ye + Attribut + ye', example: 'A ye jàngalekat ye — Il est enseignant' },
    { label: 'Négatif', formula: 'bɛ → tɛ  /  ye → ma', example: 'N tɛ taa — Je ne vais pas' },
  ],
  wf: [
    { label: 'Ordre SVO', formula: 'Sujet + Marqueur + Verbe + Objet', example: 'Dama lekk ceeb — Je mange du riz' },
    { label: 'Présent (da-)', formula: 'dama + Verbe', example: 'Dama dem — Je suis en train d\'aller' },
    { label: 'Habituel (dafay)', formula: 'dafay + Verbe', example: 'Dafay lekk — Il/Elle mange habituellement' },
    { label: 'Passé (naa)', formula: 'Verbe + naa', example: 'Dem naa — J\'y suis allé(e)' },
    { label: 'Futur (dina)', formula: 'dina / dinaa + Verbe', example: 'Dinaa dem — J\'irai' },
    { label: 'Négatif', formula: 'Verbe + ul', example: 'Demul — Il/Elle n\'est pas allé(e)' },
  ],
  la: [
    { label: 'Ordre SVO', formula: 'Préfixe sujet + Marqueur + Verbe', example: 'Nazali koliya — Je suis en train de manger' },
    { label: 'Présent progressif', formula: 'nazali + ko- + Verbe', example: 'Nazali kokende — Je suis en train d\'aller' },
    { label: 'Présent habituel', formula: 'Préfixe + Verbe + -aka', example: 'Naliyaka — Je mange (habituellement)' },
    { label: 'Passé', formula: 'Préfixe + Verbe + -aki', example: 'Nakendaki — Je suis allé(e)' },
    { label: 'Futur', formula: 'Préfixe + ko- + Verbe', example: 'Nakokende — J\'irai' },
    { label: 'Négatif', formula: 'Verbe + te', example: 'Nakendaki te — Je ne suis pas allé(e)' },
  ],
  ff: [
    { label: 'Ordre SVO', formula: 'Sujet + Marqueur + Verbe + Objet', example: 'Miɗo njoɓa ceede — Je suis en train de payer' },
    { label: 'Présent progressif', formula: 'Miɗo / Aɗa / Omo + Verbe', example: 'Miɗo yaha — Je vais' },
    { label: 'Présent habituel', formula: 'Verbe + -ay', example: 'Mi jangay — J\'étudie' },
    { label: 'Passé', formula: 'Verbe + -ii', example: 'Mi yahii — Je suis allé(e)' },
    { label: 'Futur', formula: 'Verbe + -hay', example: 'Mi yehay — J\'irai' },
    { label: 'Négatif', formula: 'Verbe + -aa', example: 'Mi yahaa — Je ne vais pas' },
  ],
  co: [
    { label: 'Ordre SVO', formula: 'Préfixe sujet + Verbe', example: 'ϯⲥⲱⲧⲉⲙ — J\'écoute' },
    { label: 'Présent', formula: 'ϯ / ⲕ / ϥ + Verbe', example: 'ϥⲥⲱⲧⲉⲙ — Il écoute' },
    { label: 'Passé', formula: 'ⲁ + Sujet + Verbe', example: 'ⲁⲓⲥⲱⲧⲉⲙ — J\'ai écouté' },
    { label: 'Futur', formula: 'Ϣⲁ + Sujet + Verbe', example: 'Ϣⲁⲓⲃⲱⲕ — J\'irai' },
    { label: 'Impératif', formula: 'Verbe seul (2e pers.)', example: 'ⲥⲱⲧⲉⲙ — Écoute !' },
  ],
  sw: [
    { label: 'Ordre SVO', formula: 'Préfixe sujet + Marqueur temps + Verbe', example: 'Ninasoma kitabu — Je lis un livre' },
    { label: 'Présent', formula: 'Ni-na- + Verbe', example: 'Ninakwenda — Je vais' },
    { label: 'Passé simple', formula: 'Ni-li- + Verbe', example: 'Nilisoma — J\'ai lu' },
    { label: 'Parfait', formula: 'Ni-me- + Verbe', example: 'Nimesoma — J\'ai déjà lu' },
    { label: 'Futur', formula: 'Ni-ta- + Verbe', example: 'Nitakwenda — J\'irai' },
    { label: 'Négatif', formula: 'Si- + Verbe', example: 'Sisomi — Je ne lis pas' },
  ],
  yo: [
    { label: 'Ordre SVO', formula: 'Sujet + Verbe + Objet', example: 'Mo fẹ́ omi — Je veux de l’eau' },
    { label: 'Présent progressif', formula: 'Sujet + ń + Verbe', example: 'Mo ń jẹun — Je mange' },
    { label: 'Passé accompli', formula: 'Sujet + ti + Verbe', example: 'Mo ti jẹun — J’ai mangé' },
    { label: 'Futur', formula: 'Sujet + máa / yóò + Verbe', example: 'Mo máa lọ — J’irai' },
    { label: 'Négatif', formula: 'Sujet + kò + Verbe', example: 'Èmi kò lọ — Je ne suis pas allé(e)' },
    { label: 'Question', formula: 'Ṣé + Phrase', example: 'Ṣé o ti jẹun? — As-tu mangé ?' },
  ],
};

const LANGUAGE_NAMES: Record<string, string> = {
  ba: 'BAMBARA', wf: 'WOLOF', la: 'LINGALA', ff: 'PEUL', co: 'COPTE', sw: 'SWAHILI', yo: 'YORUBA',
};

const GrammarInfoPanel: React.FC<{ languageCode: string }> = ({ languageCode }) => {
  const tips = GRAMMAR_TIPS[languageCode];
  const [open, setOpen] = React.useState(false);
  if (!tips) return null;
  return (
    <div style={{ margin: '0 16px 12px', borderRadius: 12, overflow: 'hidden', border: '1.5px solid var(--color-accent, #c26a2d)', background: 'var(--surface, #faf6ef)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent, #c26a2d)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.04em' }}
        aria-expanded={open}
      >
        <span><Picto name="epingle" /> COMMENT FORMER UNE PHRASE EN {LANGUAGE_NAMES[languageCode] ?? languageCode.toUpperCase()}</span>
        <span style={{ fontSize: '1rem', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: '4px 14px 12px' }}>
          {tips.map(tip => (
            <div key={tip.label} style={{ marginBottom: 10 }}>
              <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, background: 'var(--color-accent, #c26a2d)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, marginBottom: 2 }}>{tip.label}</span>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text, #2A1810)', marginBottom: 1 }}>{tip.formula}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #5C4330)', fontStyle: 'italic' }}>{tip.example}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ COMPOSANT PRINCIPAL
const SentenceConstructionGame: React.FC<SentenceConstructionGameProps> = ({
  languageCode,
  onBackToCategories,
  onAnswer,
  onGameComplete,
  onSentencesLearned,
  saveProgressOnSummary,
  userProgress,
  filterCategories,
  onSentierMode,
  onShare,
}) => {
  const { playWord } = useSupabaseAudio(languageCode);
  const languageData = getLanguageData(languageCode);


  
  
  const {
    getSessionHistory,
    isLearnedSentence
  } = useSentenceRevision(languageCode);
  
  const {
    startSession,
    addSentence,
    finishSession,
    sessionId,
    triggerXPAnimation, // ✅ NOUVEAU: Récupérer la fonction
    xpAnimationProps
  } = useSentenceXP(languageCode);

  // États du jeu
  const [mode, setMode] = useState<'category-selection' | 'category-details' | 'preview' | 'test' | 'summary'  | 'revision'>('category-selection');

  // Remonte l'état sentier (true = écran de navigation, false = exercice actif)
  React.useEffect(() => {
    onSentierMode?.(mode === 'category-selection');
  }, [mode, onSentierMode]);

  const [gameMode, setGameMode] = useState<'construction' | 'translation'>('construction');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentDetailPage, setCurrentDetailPage] = useState(0);
  const [currentSentences, setCurrentSentences] = useState<Sentence[]>([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [constructedWords, setConstructedWords] = useState<WordWithId[]>([]);
  const [availableWords, setAvailableWords] = useState<WordWithId[]>([]);
  const [attempts, setAttempts] = useState(0);
  const [showCorrection, setShowCorrection] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalPhrases, setTotalPhrases] = useState(0);
  const [learnedSentences, setLearnedSentences] = useState<LearnedSentence[]>([]);

  // États pour le mode traduction
  const [translationState, setTranslationState] = useState<TranslationState | null>(null);
  const [translationAttempts, setTranslationAttempts] = useState(0);
  const [showTranslationCorrection, setShowTranslationCorrection] = useState(false);
  const [isTranslationCorrect, setIsTranslationCorrect] = useState(false);

  // États pour les modes mélangés
  const [sentenceModes, setSentenceModes] = useState<('construction' | 'translation')[]>([]);

  // États pour le modal de confirmation
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<() => void | null>(null);

  // Stabilise le proverbe et l'encouragement — recalculés uniquement au changement de mode
  const summaryCongrats = useMemo(() => getRandomCongrats(languageCode), [mode]);
  const summaryProverb  = useMemo(() => getRandomProverb(languageCode),  [mode]);

  const [shouldSave, setShouldSave] = useState(false);
const [saveCompleted, setSaveCompleted] = useState(false);
const [hasSavedThisSession, setHasSavedThisSession] = useState(false);
const [xpGained, setXpGained] = useState(0);

  const [refreshKey, setRefreshKey] = useState(0);
  // Filtre actif pour SentenceRevisionGame : peut être la catégorie sélectionnée
  // (depuis category-details) ou le filtre d'unité (depuis le bouton global)
  const [revisionCategoryFilter, setRevisionCategoryFilter] = useState<string[] | undefined>(undefined);

 const { playSummaryAudio } = useSummaryAudio();
  // États pour tracker les tentatives
  const [sentenceAttempts, setSentenceAttempts] = useState<Record<string, {
    attempts: number;
    firstAttemptCorrect: boolean;
    totalAttempts: number;
  }>>({});

  const [summaryStep, setSummaryStep] = useState<'results' | 'xp'>('results');

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
  const handleLearnedSentencesUpdate = (event: Event) => {
    const customEvent = event as CustomEvent;
   
    
    // Forcer un re-rendu du composant
    setRefreshKey(prev => prev + 1);
    
    
  };
  
  // Écouter l'événement personnalisé
  window.addEventListener('learnedSentencesUpdated', handleLearnedSentencesUpdate);
  
  // Nettoyage
  return () => {
    window.removeEventListener('learnedSentencesUpdated', handleLearnedSentencesUpdate);
  };
}, []);

// ✅ Alternative : Écouter aussi les changements dans localStorage
useEffect(() => {
  const checkRefreshKey = () => {
    const refreshTimestamp = localStorage.getItem(`sentences-refresh-${languageCode}`);
    if (refreshTimestamp) {
   
      setRefreshKey(prev => prev + 1);
    }
  };
  
  // Vérifier toutes les 500ms (optionnel, pour backup)
  const intervalId = setInterval(checkRefreshKey, 500);
  
  return () => clearInterval(intervalId);
}, [languageCode]);



  
  const syncWithRevisionSystem = useCallback((sentences: LearnedSentence[]) => {
 
  
  const masteredSentences = sentences.filter(s => s.mastered);
  if (masteredSentences.length === 0) {
  
    return;
  }
  
  // Préparer les données pour useSentenceRevision
  const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;
  const existingData = localStorage.getItem(sentenceRevisionKey);
  const existingRevisionSentences = existingData ? JSON.parse(existingData) : [];
  
  const newRevisionSentences = masteredSentences.map(sentence => ({
    original: sentence.original,
    french: sentence.french,
    audio: sentence.audio,
    category: sentence.category || selectedCategory || 'Salutations',
    timestamp: Date.now(),
    nextReview: Date.now() + (24 * 60 * 60 * 1000),
    type: 'sentence' as const,
    words: sentence.words || sentence.original.split(' '),
    isCorrect: true
  }));
  
  // Fusionner avec les données existantes
  const mergedRevisionSentences = [...existingRevisionSentences];
  
  newRevisionSentences.forEach(newSentence => {
    const existingIndex = mergedRevisionSentences.findIndex((s: any) => 
      s.original === newSentence.original && s.category === newSentence.category
    );
    
    if (existingIndex >= 0) {
      mergedRevisionSentences[existingIndex] = {
        ...mergedRevisionSentences[existingIndex],
        ...newSentence,
        timestamp: Math.max(mergedRevisionSentences[existingIndex].timestamp, newSentence.timestamp)
      };
    } else {
      mergedRevisionSentences.push(newSentence);
    }
  });
  
  // Sauvegarder dans localStorage
  localStorage.setItem(sentenceRevisionKey, JSON.stringify(mergedRevisionSentences));
 
  
  // Émettre un événement pour notifier le hook
  window.dispatchEvent(new CustomEvent('sentencesLearned', {
    detail: {
      sentences: newRevisionSentences,
      language: languageCode
    }
  }));
}, [languageCode, selectedCategory]);



// ✅ NOUVELLE FONCTION: Lecture audio pour les phrases (identique à GrammarMode)
const handlePlayAudio = useCallback(async (audioSrc: string) => {
  try {
    // Extraire le mot du chemin audio (si c'est un chemin)
    const word = audioSrc.includes('/') 
      ? audioSrc.split('/').pop()?.replace(/\.(mp3|wav|ogg)$/, '') || audioSrc
      : audioSrc;
    
    const success = await playWord(word);
    if (!success) {
     
    }
  } catch (error) {
}
}, [playWord]);


  useFixedAppStyles('sentenceConstruction');
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  // Nettoyage au démontage
  useEffect(() => {
  
    
    return () => {
     
      
      if (saveManager.hasActiveTimers()) {
        saveManager.clearTimers();
      }
    };
  }, []);

  // Fonction pour filtrer les phrases non maîtrisées
  const filterUnlearnedSentences = useCallback((sentences: Sentence[]): Sentence[] => {
    return sentences.filter(sentence => {
      const isAlreadyMastered = isLearnedSentence(sentence.original);
      if (isAlreadyMastered) {
      
      }
      return !isAlreadyMastered;
    });
  }, [isLearnedSentence]);

  const convertToRevisionInfo = useCallback((sentence: LearnedSentence): SentenceRevisionInfo => {
    return {
      original: sentence.original,
      french: sentence.french,
      audio: sentence.audio,
      category: sentence.category,
      timestamp: sentence.timestamp || Date.now(),
      nextReview: Date.now() + (10 * 60 * 1000),
      type: 'sentence' as const,
      words: sentence.words || sentence.original.split(' '),
      isCorrect: true
    };
  }, []);

  const getLearnedSentencesCountForRevision = useCallback((): number => {
    const inUnit = (cat?: string) =>
      !filterCategories || filterCategories.length === 0 || filterCategories.includes(cat || '');

    const localKey = `sentence-revision-${languageCode}-sessionHistory`;
    const localData = localStorage.getItem(localKey);
    let localCount = 0;

    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed)) {
          localCount = parsed.filter((sentence: any) =>
            sentence.type === 'sentence' && sentence.original && sentence.french && inUnit(sentence.category)
          ).length;
        }
      } catch (error) {
}
    }

    // Fallback: compter depuis sentence-progress
    if (localCount === 0) {
      const progressKey = `sentence-progress-${languageCode}`;
      const progressData = localStorage.getItem(progressKey);
      if (progressData) {
        try {
          const sessions = JSON.parse(progressData);
          const seen = new Set<string>();
          sessions.forEach((s: any) => {
            (s.masteredSentences || []).forEach((m: any) => {
              const cat = m.category || s.category || '';
              if (m.original && m.mastered && inUnit(cat) && !seen.has(m.original)) {
                seen.add(m.original);
              }
            });
          });
          localCount = seen.size;
        } catch { /* ignore */ }
      }
    }

    return localCount;
  }, [languageCode, filterCategories]);

  const cleanupOldSavingMarkers = useCallback(() => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('saving-'));
    const now = Date.now();
    const maxAge = 10 * 60 * 1000;
    
    keys.forEach(key => {
      try {
        const timestamp = parseInt(key.split('-')[1]);
        if (now - timestamp > maxAge) {
          localStorage.removeItem(key);
       
        }
      } catch (error) {
        // Ignorer les erreurs de parsing
      }
    });
  }, []);

  useEffect(() => {
    cleanupOldSavingMarkers();
  }, [cleanupOldSavingMarkers]);

  // Fonctions utilitaires
  const cleanWord = useCallback((word: string): string => {
    return word.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  }, []);

  const cleanSentence = useCallback((sentence: string): string => {
    return sentence.split(' ').map(word => cleanWord(word)).filter(word => word.length > 0).join(' ');
  }, [cleanWord]);


  const shuffleArray = <T extends any>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const createWordsWithIds = useCallback((words: string[], displayWords?: string[]) => {
    return words.map((word, index) => ({
      id: `${word}-${index}-${Date.now()}`,
      text: word,
      displayText: displayWords?.[index],
      index
    }));
  }, []);

  const createFrenchWordsWithIds = useCallback((words: string[]): FrenchWordWithId[] => {
    return words.map((word, index) => ({
      id: `french-${word}-${index}-${Date.now()}`,
      text: word,
      index
    }));
  }, []);

  // currentFrench must already be the localized value (see prepareTranslationMode)
  // — the distractor pool below is drawn from the same localized field so the
  // whole tile set (target words + distractors) stays in one language.
  const generateDistractorWords = useCallback((currentFrench: string, allSentences: Sentence[]): string[] => {
    const currentWords = cleanSentence(currentFrench).toLowerCase().split(' ');
    const allWords = allSentences
      .map(s => getSentenceTranslation(s, i18n.language))
      .filter(french => french !== currentFrench)
      .flatMap(french => cleanSentence(french).toLowerCase().split(' '))
      .filter(word => word.length > 2 && !currentWords.includes(word));

    const uniqueWords = [...new Set(allWords)];
    const shuffled = shuffleArray(uniqueWords);
    return shuffled.slice(0, Math.max(2, Math.min(5, Math.floor(currentWords.length * 0.6))));
  }, [cleanSentence, i18n.language]);

  const generateMixedModes = useCallback((sentences: Sentence[]): ('construction' | 'translation')[] => {
  const modes: ('construction' | 'translation')[] = [];
  
  // ✅ CORRECTION: Pour chaque phrase, générer 2 modes (construction + traduction)
  sentences.forEach((sentence, index) => {
    // Alterner l'ordre des modes pour chaque phrase
    if (index % 2 === 0) {
      modes.push('construction', 'translation');
    } else {
      modes.push('translation', 'construction');
    }
  });
  

  return modes;
}, []);


  const handleShowXP = useCallback(() => {
   
    setSummaryStep('xp');
    
    // ✅ CORRECTION: Utiliser la fonction exposée par le hook
    triggerXPAnimation();
  }, [xpAnimationProps.xpGained, triggerXPAnimation]);

  // ✅ NOUVEAU: Fonction pour reset du summary state
  const resetSummaryState = useCallback(() => {
    setSummaryStep('results');
  }, []);




const handleSentenceSummaryComplete = useCallback(async () => {
 
  
  try {
    const masteredSentences = learnedSentences.filter(s => {
      const isMastered = s.mastered;
     
      return isMastered;
    });
    
   
    
    if (masteredSentences.length > 0) {
      const totalXPGained = masteredSentences.length * 15;
      
      playSummaryAudio();
       
      const wordsToAdd = masteredSentences.map(sentence => ({
        word: sentence.original,
        category: sentence.category || 'Phrases',
        subcategory: selectedCategory || '',
        language: languageCode,
        timestamp: sentence.timestamp || Date.now(),
        translation: sentence.french,
        explanation: '',
        example: sentence.french,
        audio: sentence.audio
      }));
      
      // Envoi vers la révision
      try {
        if (onSentencesLearned) {
      
          onSentencesLearned(masteredSentences);
         
        }
      } catch (revisionError) {
      }
      
      // Créer newSentenceSession AVANT de l'utiliser
      const newSentenceSession = {
        category: selectedCategory || 'Mixed',
        subcategory: selectedCategory || '',
        masteredSentences: masteredSentences.map(s => ({
          original: s.original,
          french: s.french,
          audio: s.audio,
          category: s.category || selectedCategory || 'Salutations',
          mastered: s.mastered,
          timestamp: s.timestamp || Date.now(),
          words: s.words || s.original.split(' '),
          attempts: s.attempts || 1,
          firstAttemptCorrect: s.firstAttemptCorrect
        })),
        date: Date.now(),
        totalScore: score,
        maxScore: totalPhrases * 2,
        xpGained: totalXPGained
      };
      
      // Sauvegarde localStorage (backup)
      const storageKey = `sentence-progress-${languageCode}`;
      const currentProgress = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedSentenceProgress = [...currentProgress, newSentenceSession];
      localStorage.setItem(storageKey, JSON.stringify(updatedSentenceProgress));
     
      
      // Créer l'objet de progression
      const sentenceProgress: UserProgress = {
        learnedWords: new Set(masteredSentences.map(s => s.original)),
        wordsToReview: new Set(),
        wordProgress: {},
        recentlyLearnedWords: wordsToAdd,
        language: languageCode
      };
      
     
      
      // Structure contextData conforme à SaveDataContext
      if (!saveProgressOnSummary) {
        throw new Error('saveProgressOnSummary function not available');
      }

     

      await saveProgressOnSummary(sentenceProgress, {
        mode: 'sentenceConstruction',
        description: 'Sauvegarde session Construction de Phrases',
        includeGoogleDrive: true,
        sessionStats: {
          wordsLearned: masteredSentences.length,
          totalXP: totalXPGained
        },
        sentenceSpecificData: newSentenceSession
      });
      
      if (totalXPGained > 0) {
        setXpGained(totalXPGained);
      }
      
  
      
      window.dispatchEvent(new CustomEvent('learnedSentencesUpdated', {
        detail: {
          language: languageCode,
          category: selectedCategory,
          sentencesCount: masteredSentences.length
        }
      }));

      window.dispatchEvent(new CustomEvent('sentenceProgressSynced', {
        detail: {
          language: languageCode,
          totalSessions: updatedSentenceProgress.length,
          source: 'SentenceConstruction',
          sessions: updatedSentenceProgress
        }
      }));
      
      const refreshKey = `sentences-refresh-${languageCode}`;
      localStorage.setItem(refreshKey, Date.now().toString());
      
     
      
    } else {
      
    }
    
    setSaveCompleted(true);
  
    
  } catch (error) {
    
    alert(`Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : String(error)}`);
  }
  
 
}, [
  learnedSentences,
  selectedCategory,
  languageCode,
  onSentencesLearned,
  saveProgressOnSummary,
  score,
  totalPhrases,
  setXpGained,
  setSaveCompleted,
  userProgress,
  playSummaryAudio,
  sessionId
]);



  // Démarrer la session
  useEffect(() => {
    if (mode === 'test' && currentSentences.length > 0 && !sessionId) {
      const newSessionId = startSession();
    
    }
  }, [mode, currentSentences.length, sessionId, startSession]);

useEffect(() => {
  if (mode === 'summary' && sessionId && learnedSentences.length > 0 && shouldSave && !hasSavedThisSession) {
    setHasSavedThisSession(true);
    
    const performSave = async () => {
      try {
        await handleSentenceSummaryComplete();
        setShouldSave(false);
      } catch (error) {
setHasSavedThisSession(false);
      }
    };
    
    performSave();
  }
}, [mode, sessionId, learnedSentences, shouldSave, hasSavedThisSession, handleSentenceSummaryComplete]);

 
  // Sauvegarde unifiée au résumé
 useEffect(() => {
  if (mode === 'summary' && sessionId && learnedSentences.length > 0) {
  
    
    const sessionKey = `saving-${sessionId}`;
    if (localStorage.getItem(sessionKey)) {
     
      return;
    }
    
    // ✅ NOUVEAU: Déclencher shouldSave au lieu de sauvegarder directement
    localStorage.setItem(sessionKey, 'preparing');
    setShouldSave(true);
    setHasSavedThisSession(false);
  }
}, [mode, sessionId, learnedSentences]);

  // Gestionnaires d'événements
  const handleCategorySelect = useCallback((category: string) => {
    setSelectedCategory(category);
    setCurrentDetailPage(0);
    const categorySentences = languageData.sentencesToConstruct.filter(
      sentence => sentence.category === category
    );
    
    const unlearnedSentences = filterUnlearnedSentences(categorySentences);
    
 
    
    setCurrentSentences(unlearnedSentences);
    setMode('category-details');
  }, [languageData.sentencesToConstruct, filterUnlearnedSentences]);

  // Opened from a single phrase step of the main trail (e.g. Kama
  // "Panthéon et royaume"): skip the one-node sub-trail and go straight to
  // that category's sentence list.
  const singleCategory = filterCategories && filterCategories.length === 1 ? filterCategories[0] : null;
  React.useEffect(() => {
    if (singleCategory && mode === 'category-selection') {
      handleCategorySelect(singleCategory);
    }
  }, [singleCategory, mode, handleCategorySelect]);

 

const resetSentence = useCallback((realSentenceIndex: number) => {

  
  // ✅ GARDE-FOU
  if (realSentenceIndex >= currentSentences.length || realSentenceIndex < 0) {
setMode('summary');
    return;
  }
  
  if (currentSentences[realSentenceIndex]) {
    const sentence = currentSentences[realSentenceIndex];
    const wordsToUse = sentence.words || sentence.original.split(' ');
    const glyphsToUse = sentence.hieroglyphWords;

    setConstructedWords([]);
    setAvailableWords([]);
    setShowCorrection(false);
    setIsCorrect(false);
    setAttempts(0);

    setTimeout(() => {
      // Shuffle word+glyph as paired tuples (not two independent shuffles),
      // so a Kama tile's displayed glyph always matches its scored text.
      const pairs = wordsToUse.map((w, i) => ({ w, g: glyphsToUse?.[i] }));
      const shuffled = shuffleArray(pairs);
      setAvailableWords(createWordsWithIds(
        shuffled.map(p => p.w),
        glyphsToUse ? shuffled.map(p => p.g!) : undefined,
      ));
    }, 0);
    
  
  }
}, [currentSentences, createWordsWithIds]);


   

const prepareTranslationMode = useCallback((sentence: Sentence) => {
 
  
  // ✅ GARDE-FOU
  if (!sentence) {
setMode('summary');
    return;
  }
  
  // Nettoyer l'état du mode construction
  setConstructedWords([]);
  setAvailableWords([]);
  setShowCorrection(false);
  setIsCorrect(false);
  setAttempts(0);
  
  // Localisé ici : la cible de construction, les tuiles et les distracteurs
  // (generateDistractorWords) dérivent tous de cette même valeur localisée,
  // donc ordre correct et pool de tuiles restent mutuellement cohérents.
  const localizedSentence = getSentenceTranslation(sentence, i18n.language);
  const cleanedFrench = cleanSentence(localizedSentence);
  const frenchWords = cleanedFrench.split(' ');
  const distractorWords = generateDistractorWords(localizedSentence, currentSentences);
  
  const allAvailableWords = [...frenchWords, ...distractorWords];
  const shuffledWords = shuffleArray(allAvailableWords);
  
  const translationState: TranslationState = {
    wolofSentence: sentence.original,
    correctFrenchWords: frenchWords,
    availableFrenchWords: createFrenchWordsWithIds(shuffledWords),
    constructedTranslation: [],
    distractorWords
  };
  
  setTranslationState(null);
  setTranslationAttempts(0);
  setShowTranslationCorrection(false);
  setIsTranslationCorrect(false);
  
  setTimeout(() => {
    setTranslationState(translationState);
  }, 0);
  
 
}, [currentSentences, generateDistractorWords, createFrenchWordsWithIds, cleanSentence, i18n.language]);

const cleanAllGameState = useCallback(() => {
  
  
  // État construction
  setConstructedWords([]);
  setAvailableWords([]);
  setShowCorrection(false);
  setIsCorrect(false);
  setAttempts(0);
  
  // État traduction
  setTranslationState(null);
  setTranslationAttempts(0);
  setShowTranslationCorrection(false);
  setIsTranslationCorrect(false);
}, []);


  const handleStartLearning = useCallback(() => {
  if (selectedCategory) {
    const categorySentences = languageData.sentencesToConstruct.filter(
      sentence => sentence.category === selectedCategory
    );
    
    const unlearnedSentences = filterUnlearnedSentences(categorySentences);
    
    if (unlearnedSentences.length === 0) {
   
      alert(t('sentenceConstruction.allSentencesMastered', { category: selectedCategory }));
      setMode('category-selection');
      return;
    }
    
    const shuffled = shuffleArray(unlearnedSentences).slice(0, SENTENCES_PER_LESSON);
    const modes = generateMixedModes(shuffled);
    
  
    setCurrentSentences(shuffled);
    setSentenceModes(modes);
    setTotalPhrases(shuffled.length); // ✅ IMPORTANT: totalPhrases = nombre de phrases, pas de tests
    
    if (shuffled.length > 0) {
      setMode('preview');
      setCurrentSentenceIndex(0);
      if (modes[0] === 'construction') {
        resetSentence(0);
      } else {
        prepareTranslationMode(shuffled[0]);
      }
    }
  }
}, [selectedCategory, languageData.sentencesToConstruct, resetSentence, generateMixedModes, prepareTranslationMode, filterUnlearnedSentences, t]);


    
  
const handleAnswer = useCallback(async (answerOrEvent?: string | React.MouseEvent) => {
  const currentSentenceRealIndex = Math.floor(currentSentenceIndex / 2);
  
  if (!currentSentences[currentSentenceRealIndex]) {
return;
  }

  const currentSentence = currentSentences[currentSentenceRealIndex];
  const currentMode = sentenceModes[currentSentenceIndex];
  
 
  
  // Déterminer la réponse selon le type de paramètre
  let answer: string;
  if (typeof answerOrEvent === 'string') {
    answer = answerOrEvent;
  } else {
    answer = constructedWords.map(w => cleanWord(w.text)).join(' ');
  }
  
  const constructedText = answer;
  const correctText = currentSentence.original;
  const isAnswerCorrect = constructedText.toLowerCase() === correctText.toLowerCase();


  // Gestion des tentatives par mode spécifique
  const sentenceKey = `${currentSentence.original}-${currentMode}-${currentSentenceRealIndex}`;
  const currentAttempts = sentenceAttempts[sentenceKey] || { 
    attempts: 0, 
    firstAttemptCorrect: false, 
    totalAttempts: 0 
  };
  
  currentAttempts.totalAttempts++;
  
  if (currentAttempts.attempts === 0) {
    currentAttempts.firstAttemptCorrect = isAnswerCorrect;
  }
  
  currentAttempts.attempts++;
  
  setSentenceAttempts(prev => ({
    ...prev,
    [sentenceKey]: currentAttempts
  }));
  

  
  setIsCorrect(isAnswerCorrect);
  onAnswer?.(isAnswerCorrect);

  if (isAnswerCorrect) {
    const isTrulyMastered = currentAttempts.firstAttemptCorrect && currentAttempts.attempts === 1;
    
 
    
    // Vérifier si c'est la fin des 2 modes pour cette phrase
    const isSecondModeForSentence = currentSentenceIndex % 2 === 1;
    
    if (isSecondModeForSentence) {
      // On vient de finir le 2ème mode de cette phrase
      const firstModeIndex = currentSentenceIndex - 1;
      const firstMode = sentenceModes[firstModeIndex];
      const firstModeKey = `${currentSentence.original}-${firstMode}-${currentSentenceRealIndex}`;
      const firstModeAttempts = sentenceAttempts[firstModeKey];
      const firstModeMastered = firstModeAttempts?.firstAttemptCorrect && firstModeAttempts?.attempts === 1;
      
   
      
      const isBidirectionallyMastered = firstModeMastered && isTrulyMastered;
      
      if (isBidirectionallyMastered) {
        setScore(prev => {
          const newScore = prev + 1;
        
          return newScore;
        });
      } else {
    
      }
      
      const learnedSentence: LearnedSentence = {
        original: currentSentence.original,
        french: currentSentence.french,
        audio: currentSentence.audio,
        mastered: isBidirectionallyMastered,
        timestamp: Date.now(),
        words: currentSentence.words || currentSentence.original.split(' '),
        category: currentSentence.category || selectedCategory || 'Salutations',
        interval: 1,
        attempts: (firstModeAttempts?.attempts || 0) + currentAttempts.attempts,
        firstAttemptCorrect: isBidirectionallyMastered
      };
      
   
      
      setLearnedSentences(prev => [...prev, learnedSentence]);
      addSentence(learnedSentence, score, totalPhrases);
    }
    
    // ✅ CORRECTION CRITIQUE: Vérification avant transition
    setTimeout(async () => {
    
      
      // ✅ CORRECTION: Vérification stricte de la condition
      if (currentSentenceIndex < sentenceModes.length - 1) {
        const nextIndex = currentSentenceIndex + 1;
        const nextSentenceRealIndex = Math.floor(nextIndex / 2);
        const nextMode = sentenceModes[nextIndex];
        
      
        
        // ✅ GARDE-FOU SUPPLÉMENTAIRE
        if (nextSentenceRealIndex >= currentSentences.length) {

          cleanAllGameState();
          setMode('summary');
          setShowCorrection(false);
          // onGameComplete (navigates the PARENT away, unmounting this
          // component) is deliberately NOT called here — see the summary
          // screen's exit buttons below for why.
          return;
        }

        if (!currentSentences[nextSentenceRealIndex]) {


          cleanAllGameState();
          setMode('summary');
          setShowCorrection(false);
          return;
        }
        
       
        
        cleanAllGameState();
        
        setTimeout(() => {
          setCurrentSentenceIndex(nextIndex);
          setGameMode(nextMode);
          
          if (nextMode === 'construction') {
            resetSentence(nextSentenceRealIndex);
          } else {
            prepareTranslationMode(currentSentences[nextSentenceRealIndex]);
          }
        }, 50);
      } else {
        // ✅ FIN NORMALE de tous les tests

        cleanAllGameState();
        setMode('summary');
        setShowCorrection(false);
        // onGameComplete deliberately not called here — see summary
        // screen's exit buttons.
      }
    }, 1000);
  } else {
   
    setAttempts(prev => prev + 1);
    setShowCorrection(true);
  }
}, [
  currentSentences,
  currentSentenceIndex,
  sentenceModes,
  onAnswer,
  attempts,
  resetSentence,
  prepareTranslationMode,
  onGameComplete,
  score,
  totalPhrases,
  addSentence,
  sentenceAttempts,
  constructedWords,
  cleanWord,
  learnedSentences,
  cleanAllGameState
]);

  // Gestionnaires pour le mode traduction
  const addFrenchWord = useCallback((word: FrenchWordWithId) => {
    if (!translationState) return;
    
    setTranslationState(prev => ({
      ...prev!,
      constructedTranslation: [...prev!.constructedTranslation, { ...word, index: prev!.constructedTranslation.length }],
      availableFrenchWords: prev!.availableFrenchWords.filter(w => w.id !== word.id)
    }));
  }, [translationState]);

  const removeFrenchWordByClick = useCallback((wordToRemove: FrenchWordWithId) => {
    if (isTranslationCorrect || !translationState) return;
    
    const wordIndex = translationState.constructedTranslation.findIndex(w => w.id === wordToRemove.id);
    if (wordIndex === -1) return;
    
    const newConstructedTranslation = translationState.constructedTranslation.filter(w => w.id !== wordToRemove.id);
    const updatedAvailableWords = [...translationState.availableFrenchWords, { 
      ...wordToRemove, 
      index: translationState.availableFrenchWords.length 
    }];
    
    setTranslationState(prev => ({
      ...prev!,
      constructedTranslation: newConstructedTranslation,
      availableFrenchWords: updatedAvailableWords
    }));
  }, [translationState, isTranslationCorrect]);

  const moveTranslationWord = useCallback((dragIndex: number, hoverIndex: number) => {
    if (!translationState) return;
    
    const dragWord = translationState.constructedTranslation[dragIndex];
    const newWords = [...translationState.constructedTranslation];
    newWords.splice(dragIndex, 1);
    newWords.splice(hoverIndex, 0, dragWord);
    
    setTranslationState(prev => ({
      ...prev!,
      constructedTranslation: newWords.map((word, index) => ({ ...word, index }))
    }));
  }, [translationState]);

const handleTranslationAnswer = useCallback(() => {
  if (!translationState) return;
  
  const currentSentenceRealIndex = Math.floor(currentSentenceIndex / 2);
  const currentSentence = currentSentences[currentSentenceRealIndex];
  const currentMode = sentenceModes[currentSentenceIndex];
  
  const constructedText = translationState.constructedTranslation.map(w => cleanWord(w.text)).join(' ');
  const correctText = translationState.correctFrenchWords.map(w => cleanWord(w)).join(' ');
  const isAnswerCorrect = constructedText.toLowerCase() === correctText.toLowerCase();

  // Gestion des tentatives par mode spécifique
  const sentenceKey = `${currentSentence.original}-${currentMode}-${currentSentenceRealIndex}`;
  const currentAttempts = sentenceAttempts[sentenceKey] || { 
    attempts: 0, 
    firstAttemptCorrect: false, 
    totalAttempts: 0 
  };
  
  currentAttempts.totalAttempts++;
  
  if (currentAttempts.attempts === 0) {
    currentAttempts.firstAttemptCorrect = isAnswerCorrect;
  }
  
  currentAttempts.attempts++;
  
  setSentenceAttempts(prev => ({
    ...prev,
    [sentenceKey]: currentAttempts
  }));
  
  setIsTranslationCorrect(isAnswerCorrect);
  onAnswer?.(isAnswerCorrect);

  if (isAnswerCorrect) {
    const isTrulyMastered = currentAttempts.firstAttemptCorrect && currentAttempts.attempts === 1;
    
    // Même logique que handleAnswer pour la fin de phrase
    const isSecondModeForSentence = currentSentenceIndex % 2 === 1;
    
    if (isSecondModeForSentence) {
      const firstModeIndex = currentSentenceIndex - 1;
      const firstMode = sentenceModes[firstModeIndex];
      const firstModeKey = `${currentSentence.original}-${firstMode}-${currentSentenceRealIndex}`;
      const firstModeAttempts = sentenceAttempts[firstModeKey];
      const firstModeMastered = firstModeAttempts?.firstAttemptCorrect && firstModeAttempts?.attempts === 1;
      
      const isBidirectionallyMastered = firstModeMastered && isTrulyMastered;
      
      if (isBidirectionallyMastered) {
        setScore(prev => prev + 1);
     
      }
      
      const learnedSentence: LearnedSentence = {
        original: currentSentence.original,
        french: currentSentence.french,
        audio: currentSentence.audio,
        mastered: isBidirectionallyMastered,
        timestamp: Date.now(),
        words: currentSentence.words || currentSentence.original.split(' '),
        category: currentSentence.category || selectedCategory || 'Salutations',
        interval: 1,
        attempts: (firstModeAttempts?.attempts || 0) + currentAttempts.attempts,
        firstAttemptCorrect: isBidirectionallyMastered
      };
      
      setLearnedSentences(prev => [...prev, learnedSentence]);
      addSentence(learnedSentence, score, totalPhrases);
    }
    
    // ✅ MÊME CORRECTION CRITIQUE pour handleTranslationAnswer
    setTimeout(() => {
     
      
      if (currentSentenceIndex < sentenceModes.length - 1) {
        const nextIndex = currentSentenceIndex + 1;
        const nextSentenceRealIndex = Math.floor(nextIndex / 2);
        const nextMode = sentenceModes[nextIndex];
        
        // ✅ GARDE-FOU IDENTIQUE
        if (nextSentenceRealIndex >= currentSentences.length || !currentSentences[nextSentenceRealIndex]) {

          cleanAllGameState();
          setMode('summary');
          setShowTranslationCorrection(false);
          // onGameComplete deliberately not called here — see summary
          // screen's exit buttons.
          return;
        }
        
        cleanAllGameState();
        
        setTimeout(() => {
          setCurrentSentenceIndex(nextIndex);
          setGameMode(nextMode);
          
          if (nextMode === 'construction') {
            resetSentence(nextSentenceRealIndex);
          } else {
            prepareTranslationMode(currentSentences[nextSentenceRealIndex]);
          }
        }, 50);
      } else {

        cleanAllGameState();
        setMode('summary');
        setShowTranslationCorrection(false);
        // onGameComplete deliberately not called here — see summary
        // screen's exit buttons.
      }
    }, 1500);
  } else {
    setTranslationAttempts(prev => prev + 1);
 
    setShowTranslationCorrection(true);
  }
}, [
  translationState, 
  cleanWord,
  onAnswer, 
  currentSentenceIndex, 
  currentSentences, 
  sentenceModes,
  translationAttempts, 
  resetSentence,
  prepareTranslationMode,
  addSentence,
  totalPhrases,
  score,
  sentenceAttempts,
  onGameComplete,
  cleanAllGameState
]);

const handleSkipSentence = useCallback(() => {
  // ✅ NOUVEAU: Fonction pour passer à la phrase/mode suivant après échec
 
  
  // ✅ CORRECTION: Nettoyer l'état avant le passage
  cleanAllGameState();
  
  // Passer au test suivant dans l'ordre des modes
  if (currentSentenceIndex < sentenceModes.length - 1) {
    const nextIndex = currentSentenceIndex + 1;
    const nextSentenceRealIndex = Math.floor(nextIndex / 2);
    const nextMode = sentenceModes[nextIndex];
    
 
    // ✅ Attendre le nettoyage avant de changer
    setTimeout(() => {
      setCurrentSentenceIndex(nextIndex);
      setGameMode(nextMode);
      
      if (nextMode === 'construction') {
        resetSentence(nextSentenceRealIndex);
      } else {
        prepareTranslationMode(currentSentences[nextSentenceRealIndex]);
      }
    }, 50);
  } else {
    // ✅ Fin de tous les tests
   
    
    // Créer une LearnedSentence pour la dernière phrase échouée si nécessaire
    const currentSentence = currentSentences[Math.floor(currentSentenceIndex / 2)];
    const isEndOfSentence = currentSentenceIndex % 2 === 1;
    
    if (isEndOfSentence && currentSentence) {
      const previousModeKey = `${currentSentence.original}-${currentSentenceIndex - 1}`;
      const previousAttempts = sentenceAttempts[previousModeKey];
      const previousMastered = previousAttempts?.firstAttemptCorrect && previousAttempts?.attempts === 1;
      
      const learnedSentence: LearnedSentence = {
        original: currentSentence.original,
        french: currentSentence.french,
        audio: currentSentence.audio,
        mastered: false, // ✅ Pas maîtrisée car échouée
        timestamp: Date.now(),
        words: currentSentence.words || currentSentence.original.split(' '),
        category: currentSentence.category || selectedCategory || 'Salutations',
        interval: 1,
        attempts: (previousAttempts?.attempts || 1) + (attempts + 1 || translationAttempts + 1),
        firstAttemptCorrect: false
      };
      
     
      
      setLearnedSentences(prev => [...prev, learnedSentence]);
      addSentence(learnedSentence, score, totalPhrases);
    }
    
    cleanAllGameState();
    setMode('summary');
    // onGameComplete deliberately not called here — see summary screen's
    // exit buttons.
  }
}, [
  currentSentenceIndex,
  sentenceModes,
  currentSentences,
  sentenceAttempts,
  attempts,
  translationAttempts,
  resetSentence,
  prepareTranslationMode,
  score,
  totalPhrases,
  addSentence,
  onGameComplete,
  cleanAllGameState // ✅ AJOUT
]);
  const moveWord = useCallback((dragIndex: number, hoverIndex: number) => {
    const dragWord = constructedWords[dragIndex];
    const newWords = [...constructedWords];
    newWords.splice(dragIndex, 1);
    newWords.splice(hoverIndex, 0, dragWord);
    setConstructedWords(newWords.map((word, index) => ({ ...word, index })));
  }, [constructedWords]);

  const addWord = useCallback((word: WordWithId) => {
    setConstructedWords(prev => [...prev, { ...word, index: prev.length }]);
    setAvailableWords(prev => prev.filter(w => w.id !== word.id));
  }, []);

  const removeWordByClick = useCallback((wordToRemove: WordWithId) => {
    if (isCorrect) return;
    
    const wordIndex = constructedWords.findIndex(w => w.id === wordToRemove.id);
    if (wordIndex === -1) return;
    
    const newConstructedWords = constructedWords.filter(w => w.id !== wordToRemove.id);
    setConstructedWords(newConstructedWords);
    
    setAvailableWords(prev => [...prev, { ...wordToRemove, index: prev.length }]);
  }, [constructedWords, isCorrect]);

  const handleNavigationWithConfirmation = useCallback((navigationCallback: () => void) => {
    const isInActiveSession = (mode === 'test' || mode === 'preview') && currentSentenceIndex < currentSentences.length;
    const hasProgress = learnedSentences.length > 0 || score > 0;
    
    if (isInActiveSession || hasProgress) {
     
      
      setPendingNavigation(() => navigationCallback);
      setShowExitModal(true);
    } else {
      navigationCallback();
    }
  }, [mode, currentSentenceIndex, currentSentences.length, learnedSentences.length, score]);

  const handleExitConfirm = useCallback(() => {
    setShowExitModal(false);
    
   
    
    setLearnedSentences([]);
    
    const tempSessionKey = `${languageCode}-temp-session`;
    localStorage.removeItem(tempSessionKey);
  
    
    saveManager.reset();
    
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, languageCode]);

  const handleExitCancel = useCallback(() => {
    setShowExitModal(false);
    setPendingNavigation(null);
  }, []);

const continueTrainingWithoutSave = useCallback(async () => {

  
  // IMPORTANT: Synchroniser d'abord les phrases maîtrisées
  if (learnedSentences.length > 0) {
    syncWithRevisionSystem(learnedSentences);
    
    // Attendre que localStorage soit mis à jour
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  // Réinitialiser l'état de la session
  setScore(0);
  setIsCorrect(false);
  setShowCorrection(false);
  setCurrentSentenceIndex(0);
  setConstructedWords([]);
  setAvailableWords([]);
  setLearnedSentences([]);
  setSentenceAttempts({});
  resetSummaryState();
  
  // Déterminer les phrases disponibles
  let allSentences: Sentence[] = [];
  
  if (selectedCategory) {
    allSentences = languageData.sentencesToConstruct.filter(
      sentence => sentence.category === selectedCategory
    );
  }
  
  // Filtrer les phrases déjà apprises - VÉRIFIER DANS LOCALSTORAGE DIRECTEMENT
  const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;
  const localData = localStorage.getItem(sentenceRevisionKey);
  const learnedSentencesList = new Set<string>();
  
  if (localData) {
    try {
      const parsed = JSON.parse(localData);
      parsed.forEach((s: any) => {
        if (s.type === 'sentence' && s.original) {
          learnedSentencesList.add(s.original);
        }
      });
    } catch (error) {
}
  }
  
  // Filtrer également avec le hook pour être sûr
  const unlearnedSentences = allSentences.filter(sentence => {
    const isInLocalStorage = learnedSentencesList.has(sentence.original);
    const isInHook = isLearnedSentence(sentence.original);
    
    if (isInLocalStorage || isInHook) {
    
      return false;
    }
    return true;
  });
  
 
  
  if (unlearnedSentences.length === 0) {
   
    alert(t('sentenceConstruction.allMastered'));
    setMode('category-selection');
    return;
  }
  
  // Sélectionner et mélanger les phrases
  const shuffled = shuffleArray(unlearnedSentences).slice(0, SENTENCES_PER_LESSON);
  const modes = generateMixedModes(shuffled);
  
  setCurrentSentences(shuffled);
  setSentenceModes(modes);
  setTotalPhrases(shuffled.length);
  setMode('preview');
  
  // Préparer le premier élément
  setTimeout(() => {
    if (modes[0] === 'construction') {
      resetSentence(0);
    } else {
      prepareTranslationMode(shuffled[0]);
    }
  }, 100);
}, [
  selectedCategory,
  languageData.sentencesToConstruct,
  resetSentence,
  prepareTranslationMode,
  generateMixedModes,
  mode,
  isLearnedSentence,
  languageCode,
  resetSummaryState,
  learnedSentences,
  syncWithRevisionSystem  // Ajouter dans les dépendances
]);



  const handleBackToCategoriesWithoutSave = useCallback(async () => {
    
    
    // ✅ CORRECTION: Attendre que la sauvegarde soit vraiment terminée
    if (hasSavedThisSession) {
    
      
      // Attendre un peu pour s'assurer que localStorage est à jour
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Forcer le rafraîchissement de l'interface
      setRefreshKey(prev => {
        const newKey = prev + 1;
        
        return newKey;
      });
    }
    
    resetSummaryState();
    setMode('category-selection');
  }, [hasSavedThisSession, resetSummaryState]);
  
  if (isLoading) {
    return (
      <div className="modern-sentence-game">
        <div className="loading-overlay">Chargement en cours...</div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={(mode === 'test' || mode === 'preview') ? 'lm-screen' : `modern-sentence-game top-aligned${(mode === 'category-selection' || mode === 'category-details' || mode === 'summary' || mode === 'revision') ? ' modern-sentence-game--sentier' : ''}`} data-theme={theme}>
        {mode === 'preview' && <TribalBorder />}
        <div className="modern-game-container">
          {mode !== 'revision' && mode !== 'category-selection' && mode !== 'category-details' && (
  <header className="modern-game-header" style={{ marginTop: 0, paddingTop: '1rem' }}>
    <FloatingBackButton
      onBack={() => {
        if (mode === 'summary') {
          setMode('category-selection');
          setSelectedCategory(null);
        } else if (mode === 'preview') {
          handleNavigationWithConfirmation(() => {
            if (selectedCategory) {
              setMode('category-details');
            } else {
              setMode('category-selection');
            }
          });
        } else if (mode === 'test') {
          handleNavigationWithConfirmation(() => {
            if (selectedCategory) {
              setMode('category-details');
            } else {
              setMode('category-selection');
            }
          });
        }
      }}
      position="top-left"
      variant="minimal"
     
    />
  </header>
)}

          {/* ✅ ANIMATION XP INTÉGRÉE - SEULEMENT en mode summary et étape xp */}
          {mode === 'summary' && summaryStep === 'xp' && (
            <XPAnimation {...xpAnimationProps} />
          )}


        {mode === 'category-selection' && !singleCategory && (() => {
          const phraseCategories = Object.entries(languageData.sentenceCategories).filter(([cat]) =>
            !filterCategories || filterCategories.length === 0 || filterCategories.includes(cat)
          );

          // Cosmetic-only label override, Kama-specific: the underlying key
          // must stay the exact 'Voyages' string (UNIT_SENTENCE_CATEGORIES
          // in CategorySelection.tsx matches it verbatim to place this
          // node under unit 3), but "Voyages" reads as a modern-travel
          // category — other languages (Wolof, etc.) genuinely use it for
          // that, so the override is gated on languageCode to avoid
          // relabeling their real "Voyages" category too.
          const categoryDisplayLabel: Record<string, string> =
            languageCode === 'ka' ? { 'Voyages': 'Le Nil et le temps' } : {};

          const phrTrailSteps = phraseCategories.map(([category, info]) => {
            const categorySentences = languageData.sentencesToConstruct.filter(
              s => s.category === category
            );
            if (refreshKey >= 0) {}
            const learnedCount = categorySentences.filter(s => isLearnedSentence(s.original)).length;
            const total = categorySentences.length;
            const status: 'done' | 'in_progress' | 'available' =
              learnedCount === total && total > 0 ? 'done' :
              learnedCount > 0 ? 'in_progress' : 'available';
            return { id: category, label: categoryDisplayLabel[category] ?? category, icon: (info as any).icon as string, status, learnedCount, total };
          });

          const phrContinueStep =
            phrTrailSteps.find(s => s.status === 'in_progress') ??
            phrTrailSteps.find(s => s.status === 'available');

          const phrDoneCount = phrTrailSteps.filter(s => s.status === 'done').length;
          const revisionCount = getLearnedSentencesCountForRevision();

          return (
            <div className="phr-sentier-screen" data-theme={theme}>
              <TribalBorder />
              <FloatingBackButton
                onBack={onBackToCategories}
                position="top-left"
                variant="minimal"
              />
              {/* En-tête */}
              <div className="phr-sentier-header">
                <p className="phr-sentier-header__sub">PHRASES</p>
                <h1 className="phr-sentier-header__title">{languageData.name}</h1>
                <p className="phr-sentier-header__stats">
                  {phrTrailSteps.length} {phrTrailSteps.length > 1 ? 'catégories' : 'catégorie'} · {phrDoneCount} {phrDoneCount > 1 ? 'maîtrisées' : 'maîtrisée'}
                </p>
              </div>

              {/* Arches décoratives */}
              <div className="phr-sentier-arches" aria-hidden="true" />

              {/* Encart info structure grammaticale */}
              <GrammarInfoPanel languageCode={languageCode} />

              {/* Bouton révision globale */}
              {revisionCount > 0 && (
                <div className="phr-sentier-revision">
                  <button
                    className="phr-sentier-revision__btn"
                    onClick={() => {
                      setRevisionCategoryFilter(filterCategories && filterCategories.length > 0 ? filterCategories : undefined);
                      setMode('revision');
                    }}
                  >
                    ↺ Réviser toutes mes phrases ({revisionCount})
                  </button>
                </div>
              )}

              {/* Sentier */}
              <div className="phr-sentier-trail">
                {phrTrailSteps.map((step, i) => {
                  const isLeft     = i % 2 === 0;
                  const isLast     = i === phrTrailSteps.length - 1;
                  const isContinue = step.id === phrContinueStep?.id;

                  let nodeClass = 'phr-sentier-node';
                  if (step.status === 'done')            nodeClass += ' phr-sentier-node--done';
                  else if (isContinue)                   nodeClass += ' phr-sentier-node--active';
                  else if (step.status === 'in_progress') nodeClass += ' phr-sentier-node--progress';
                  else                                   nodeClass += ' phr-sentier-node--available';

                  const subLabel = step.learnedCount > 0
                    ? `${step.learnedCount}/${step.total}${step.status === 'in_progress' ? ' · continuer' : ''}`
                    : `${step.total} phrases`;

                  return (
                    <div
                      key={step.id}
                      className={`phr-sentier-step ${isLeft ? 'phr-sentier-step--left' : 'phr-sentier-step--right'}`}
                    >
                      {!isLast && (
                        <div className={`phr-sentier-conn ${isLeft ? 'phr-sentier-conn--lr' : 'phr-sentier-conn--rl'}`} />
                      )}
                      <motion.button
                        className={nodeClass}
                        onClick={() => handleCategorySelect(step.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.93 }}
                      >
                        {step.status === 'done'
                          ? <span>✓</span>
                          : isContinue
                            ? <span className="phr-sentier-play-icon">▶</span>
                            : <span className="phr-sentier-node__icon"><EmojiText text={step.icon} /></span>}
                      </motion.button>
                      <div className="phr-sentier-label">
                        <p className="phr-sentier-label__name">{step.label}</p>
                        <p className="phr-sentier-label__sub">{subLabel}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })()}


          {mode === 'revision' && (
            <SentenceRevisionGame
              languageCode={languageCode}
              onBackToCategories={() => setMode('category-selection')}
              onAnswer={onAnswer}
              userProgress={userProgress}
              disableConfirmationModal={true}
              filterCategories={revisionCategoryFilter}
            />
          )}

        

             
         

       {mode === 'category-details' && selectedCategory && (() => {
  const allCatSentences = languageData.sentencesToConstruct.filter(s => s.category === selectedCategory);
  const learnedSentences = allCatSentences.filter(s => isLearnedSentence(s.original));
  const totalCount = allCatSentences.length;
  const learnedCount = learnedSentences.length;
  const detailItemsPerPage = 5;
  const detailTotalPages = Math.ceil(allCatSentences.length / detailItemsPerPage);
  const detailStartIndex = currentDetailPage * detailItemsPerPage;
  const visibleSentences = allCatSentences.slice(detailStartIndex, detailStartIndex + detailItemsPerPage);
  const detailPageNumbers: number[] = [];
  for (let i = 0; i < detailTotalPages; i++) detailPageNumbers.push(i);
  const progressPct = totalCount > 0 ? Math.round((learnedCount / totalCount) * 100) : 0;
  const isCategoryDone = learnedCount === totalCount && totalCount > 0;

  return (
    <div className="phr-detail-screen" data-theme={theme}>
      <TribalBorder />
      <FloatingBackButton
        onBack={() => {
          if (singleCategory) { onBackToCategories(); return; }
          setMode('category-selection');
          setSelectedCategory(null);
        }}
        position="top-left"
        variant="minimal"
      />

      {/* En-tête */}
      <div className="phr-detail-topbar">
        <p className="phr-detail-topbar__sub">{t('sentenceConstruction.categorySectionLabel')}</p>
        <h1 className="phr-detail-topbar__title">
          {languageCode === 'ka' && selectedCategory === 'Voyages' ? 'Le Nil et le temps' : selectedCategory}
        </h1>
        <div className="phr-detail-progress-bar">
          <motion.div
            className="phr-detail-progress-bar__fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
        <p className="phr-detail-topbar__count">{t('sentenceConstruction.learnedCount', { learned: learnedCount, total: totalCount })}</p>
      </div>

      {/* Boutons d'action */}
      <div className="phr-detail-actions">
        {isCategoryDone ? (
          <motion.button
            className="phr-detail-btn phr-detail-btn--accent"
            onClick={() => { setRevisionCategoryFilter([selectedCategory]); setMode('revision'); }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('sentenceConstruction.reviewThese')}
          </motion.button>
        ) : (
          <motion.button
            className="phr-detail-btn phr-detail-btn--accent"
            onClick={handleStartLearning}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {learnedCount > 0
              ? t('sentenceConstruction.continueLearningCount', { count: totalCount - learnedCount })
              : t('sentenceConstruction.startLearningCount', { count: totalCount - learnedCount })}
          </motion.button>
        )}
        {learnedCount > 0 && !isCategoryDone && (
          <motion.button
            className="phr-detail-btn phr-detail-btn--ghost"
            onClick={() => { setRevisionCategoryFilter([selectedCategory]); setMode('revision'); }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('sentenceConstruction.review', { count: learnedCount })}
          </motion.button>
        )}
      </div>

      {/* Liste des phrases */}
      <div className="phr-detail-section">
        <div className="phr-detail-section-header">
          <span className="phr-detail-section-title">{t('sentenceConstruction.categorySentences')}</span>
        </div>

        <div className="phr-detail-deco-border" aria-hidden="true" />

        <div>
          {visibleSentences.map((sentence, index) => {
            const isLearned = isLearnedSentence(sentence.original);
            return (
              <div
                key={detailStartIndex + index}
                className={`phr-detail-row${isLearned ? ' phr-detail-row--learned' : ''}`}
              >
                <div className="phr-detail-row__text">
                  {sentence.hieroglyphs && (
                    <span className="phr-detail-row__glyphs" lang="egy">{sentence.hieroglyphs}</span>
                  )}
                  <span className="phr-detail-row__original">{sentence.original}</span>
                  <span className="phr-detail-row__french">{getSentenceTranslation(sentence, i18n.language)}</span>
                </div>
                <div className="phr-detail-row__right">
                  {isLearned && <span className="phr-detail-check">✓</span>}
                  {hasValidAudio(sentence.audio) && (
                    <motion.button
                      className="phr-detail-audio-btn"
                      onClick={() => handlePlayAudio(sentence.audio!)}
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label={t('learnMode.listenButton') as string}
                    >
                      <Volume2 size={16} />
                    </motion.button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {detailTotalPages > 1 && (
          <div className="phr-detail-pagination">
            <motion.button
              className="phr-detail-page-btn"
              onClick={() => setCurrentDetailPage(p => Math.max(0, p - 1))}
              disabled={currentDetailPage === 0}
              whileHover={currentDetailPage !== 0 ? { scale: 1.1 } : {}}
              aria-label={t('sentenceConstruction.prevPage') as string}
            >
              <ChevronLeft size={18} />
            </motion.button>

            {detailPageNumbers.map(p => (
              <motion.button
                key={p}
                className={`phr-detail-page-num${p === currentDetailPage ? ' active' : ''}`}
                onClick={() => setCurrentDetailPage(p)}
                whileHover={p !== currentDetailPage ? { y: -2 } : {}}
              >
                {p + 1}
              </motion.button>
            ))}

            <motion.button
              className="phr-detail-page-btn"
              onClick={() => setCurrentDetailPage(p => Math.min(detailTotalPages - 1, p + 1))}
              disabled={currentDetailPage === detailTotalPages - 1}
              whileHover={currentDetailPage !== detailTotalPages - 1 ? { scale: 1.1 } : {}}
              aria-label={t('sentenceConstruction.nextPage') as string}
            >
              <ChevronRight size={18} />
            </motion.button>
          </div>
        )}

        <div className="phr-detail-deco-border phr-detail-deco-border--bottom" aria-hidden="true" />
      </div>
    </div>
  );
})()}


         {mode === 'preview' && currentSentences[currentSentenceIndex] && (
  <div className="lm-test">
    <div className="lm-topbar">
      <div className="lm-topbar__track">
        <div className="lm-topbar__fill" style={{ width: `${((currentSentenceIndex + 1) / currentSentences.length) * 100}%` }} />
      </div>
      <span className="lm-topbar__count">{currentSentenceIndex + 1}/{currentSentences.length}</span>
    </div>
    <p className="lm-arch-card__label">{t('sentenceConstruction.learnSentence')}</p>
    {currentSentences[currentSentenceIndex].hieroglyphs && (
      <p className="lm-hieroglyph-line">{currentSentences[currentSentenceIndex].hieroglyphs}</p>
    )}
    <h2 className="lm-arch-card__word">{currentSentences[currentSentenceIndex].original}</h2>
    {hasValidAudio(currentSentences[currentSentenceIndex].audio) && (
      <div className="lm-audio-wrap">
        <motion.button
          className="lm-audio-btn"
          onClick={() => handlePlayAudio(currentSentences[currentSentenceIndex].audio!)}
          whileTap={{ scale: 0.93 }}
        >
          <Volume2 size={20} />
        </motion.button>
        <span className="lm-audio-label">{t('learnMode.listenButton')}</span>
      </div>
    )}
    <p className="lm-arch-card__translation">{getSentenceTranslation(currentSentences[currentSentenceIndex], i18n.language)}</p>

    <div className="lm-preview-actions">
      <button
        onClick={() => {
          if (currentSentenceIndex < currentSentences.length - 1) {
            setCurrentSentenceIndex(prev => prev + 1);
          } else {
            const currentMode = sentenceModes[0];
            setGameMode(currentMode);
            setMode('test');
            setCurrentSentenceIndex(0);

            if (currentMode === 'construction') {
              resetSentence(0);
            } else {
              prepareTranslationMode(currentSentences[0]);
            }
          }
        }}
        className={currentSentenceIndex === currentSentences.length - 1 ? "lm-next-btn lm-next-btn--start" : "lm-next-btn"}
      >
        {currentSentenceIndex === currentSentences.length - 1 ? (
          <>
            <CheckCircle size={16} />
            {t('sentenceConstruction.startExercises')}
          </>
        ) : (
          <>
            {t('learnMode.nextWord')}
            <ChevronRight />
          </>
        )}
      </button>
    </div>
  </div>
)}

         {/* ✅ MODE TEST - CONSTRUCTION */}
{mode === 'test' && gameMode === 'construction' && (
  <div className="lm-test">
    <div className="lm-topbar">
      <div className="lm-topbar__track">
        <div className="lm-topbar__fill" style={{ width: `${((Math.floor(currentSentenceIndex / 2) + 1) / currentSentences.length) * 100}%` }} />
      </div>
      <span className="lm-topbar__count">{Math.floor(currentSentenceIndex / 2) + 1}/{currentSentences.length}</span>
    </div>
    {(() => {
      const currentSentenceRealIndex = Math.floor(currentSentenceIndex / 2);
      const currentSentence = currentSentences[currentSentenceRealIndex];

      // ✅ GARDE-FOU: Vérifier que la phrase existe
      if (!currentSentence) {
return (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('sentenceConstruction.sentenceNotFound')}</p>
            <button onClick={() => setMode('summary')} className="modern-primary-button">
              {t('sentenceConstruction.skipToSummary')}
            </button>
          </div>
        );
      }

      return (
        <>
          <span className="lm-arch-card__deco-top">◇ ◁ ◇ ▷ ◇ ◁ ◇ ▷ ◇</span>
          <p className="lm-arch-card__label">{t('sentenceConstruction.reconstructSentence')}</p>
          {/* No hieroglyph line here (unlike the preview/correction/translate
              screens): the tiles below now render the glyphs themselves, in
              scrambled order — showing the whole correctly-ordered phrase
              here as well would hand the reader the answer outright. */}
          <h2 className="lm-arch-card__word">{getSentenceTranslation(currentSentence, i18n.language)}</h2>
          <span className="lm-arch-card__deco-bottom">∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨</span>

          <div className={`modern-construction-area ${isCorrect ? 'success' : ''}`}>
            {constructedWords.map((word, index) => (
              <DraggableWord
                key={word.id}
                word={word}
                index={index}
                moveWord={moveWord}
                isCorrect={isCorrect}
                onWordClick={removeWordByClick}
              />
            ))}
          </div>

          <div className="modern-available-words">
            {availableWords.map((word) => (
              <button
                key={word.id}
                onClick={() => addWord(word)}
                className="modern-word-item"
                disabled={isCorrect}
              >
                {word.text}
              </button>
            ))}
          </div>

          <div className="lm-sentence-actions">
            <button
              onClick={() => handleAnswer(constructedWords.map(w => w.text).join(' '))}
              disabled={constructedWords.length === 0 || isCorrect || showCorrection}
              className="lm-next-btn lm-verify-btn"
            >
              <CheckCircle size={16} /> {t('exercise.verify')}
            </button>
          </div>

          {showCorrection && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lm-correction-panel"
            >
              <span className="lm-correction-panel__label">
                {isCorrect ? t('grammar.correct') : t('grammar.goodAnswer')}
              </span>
              {currentSentence.hieroglyphs && (
                <p className="lm-hieroglyph-line">{currentSentence.hieroglyphs}</p>
              )}
              <p className="lm-correction-panel__answer">{currentSentence.original}</p>
              <div className="lm-correction-panel__actions">
                <button onClick={handleSkipSentence} className="lm-next-btn">
                  {t('sentenceConstruction.nextArrow')}
                </button>
              </div>
            </motion.div>
          )}
        </>
      );
    })()}
  </div>
)}

{mode === 'test' && gameMode === 'translation' && translationState && (
  <div className="lm-test">
    <div className="lm-topbar">
      <div className="lm-topbar__track">
        <div className="lm-topbar__fill" style={{ width: `${((Math.floor(currentSentenceIndex / 2) + 1) / currentSentences.length) * 100}%` }} />
      </div>
      <span className="lm-topbar__count">{Math.floor(currentSentenceIndex / 2) + 1}/{currentSentences.length}</span>
    </div>
    {(() => {
      const currentSentenceRealIndex = Math.floor(currentSentenceIndex / 2);
      const currentSentence = currentSentences[currentSentenceRealIndex];

      if (!currentSentence) {
        return (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('sentenceConstruction.sentenceNotFound')}</p>
            <button onClick={() => setMode('summary')} className="modern-primary-button">
              {t('sentenceConstruction.skipToSummary')}
            </button>
          </div>
        );
      }

      return (
        <>
          <span className="lm-arch-card__deco-top">◇ ◁ ◇ ▷ ◇ ◁ ◇ ▷ ◇</span>
          <p className="lm-arch-card__label">{t('sentenceConstruction.translateIntoFrenchNoColon')}</p>
          {currentSentence.hieroglyphs && (
            <p className="lm-hieroglyph-line">{currentSentence.hieroglyphs}</p>
          )}
          <h2 className="lm-arch-card__word">{translationState.wolofSentence}</h2>
          {currentSentence.audio && hasValidAudio(currentSentence.audio) && (
            <div className="lm-audio-wrap">
              <motion.button className="lm-audio-btn" onClick={() => handlePlayAudio(currentSentence.audio!)} whileTap={{ scale: 0.93 }}>
                <Volume2 size={20} />
              </motion.button>
              <span className="lm-audio-label">{t('learnMode.listenButton')}</span>
            </div>
          )}
          <span className="lm-arch-card__deco-bottom">∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨</span>

          <TranslationDropZone
            constructedWords={translationState.constructedTranslation}
            onWordDrop={addFrenchWord}
            moveWord={moveTranslationWord}
            isCorrect={isTranslationCorrect}
            onWordClick={removeFrenchWordByClick}
          />

          <div className="modern-available-words french-words-container">
            <div className="french-words-grid">
              {translationState.availableFrenchWords.map((word) => (
                <DraggableFrenchWord
                  key={word.id}
                  word={word}
                  index={word.index}
                  moveWord={() => {}}
                  isCorrect={false}
                  source="available"
                  onWordClick={addFrenchWord}
                />
              ))}
            </div>
          </div>

          <div className="lm-sentence-actions">
            <button
              onClick={handleTranslationAnswer}
              disabled={translationState.constructedTranslation.length === 0 || isTranslationCorrect}
              className="lm-next-btn lm-verify-btn"
            >
              <CheckCircle size={16} /> {t('exercise.verify')}
            </button>
          </div>

          {showTranslationCorrection && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lm-correction-panel"
            >
              <span className="lm-correction-panel__label">
                {isTranslationCorrect ? t('grammar.correct') : t('grammar.goodAnswer')}
              </span>
              <p className="lm-correction-panel__answer">{translationState.correctFrenchWords.join(' ')}</p>
              <div className="lm-correction-panel__actions">
                <button onClick={handleSkipSentence} className="lm-next-btn">
                  {t('sentenceConstruction.nextArrow')}
                </button>
              </div>
            </motion.div>
          )}
        </>
      );
    })()}
  </div>
)}

          {/* ✅ MODE SUMMARY AVEC SYSTÈME À DEUX ÉTAPES */}
         {/* ✅ MODE SUMMARY AVEC SYSTÈME À DEUX ÉTAPES */}
{mode === 'summary' && (() => {
  const completed = learnedSentences.length;
  const mastery = completed > 0 ? Math.round((score / completed) * 100) : 0;
  const congrats = summaryCongrats;
  const proverb  = summaryProverb;
  const masteredList = learnedSentences.filter(s => s.mastered);
  const reviewList   = learnedSentences.filter(s => !s.mastered);
  return (
    <SummaryScreen
      theme={theme}
      label={t('sentenceConstruction.summaryLabel')}
      congrats={congrats}
      stats={[
        { value: `${mastery}%`, label: t('sentenceConstruction.mastery') },
        { value: `${score}/${completed}`, label: t('sentenceConstruction.succeeded') },
        { value: `${completed}`, label: t('sentenceConstruction.learnedStat') },
      ]}
      proverb={proverb}
      primaryBtn={{
        label: t('learnMode.summary.continueLearning'),
        // handleStartLearning stays local to this component (loads the next
        // batch of unlearned sentences in the same category, or falls back
        // to 'category-selection' — never navigates the parent App away),
        // so onGameComplete must NOT also fire here: it would race
        // handleStartLearning's own navigation with the parent's
        // navigateAfterPhrases, landing on whichever wins unpredictably
        // (confirmed happening on secondaryBtn below before this fix).
        onClick: handleStartLearning,
      }}
      secondaryBtn={{
        label: t('sentenceConstruction.backToTrail', { sentier: getSentierName(languageCode) }),
        // Same reasoning: onBackToCategories already IS the explicit "go
        // back to the sentier" navigation — calling onGameComplete
        // alongside it raced App.tsx's own navigateAfterPhrases (which
        // jumps to "the next vocab category" instead), so the reader
        // sometimes landed on a random vocab lesson instead of the sentier.
        onClick: onBackToCategories,
      }}
      onBack={() => { setMode('category-selection'); setSelectedCategory(null); }}
      onShare={onShare}
    >
      {masteredList.length > 0 && (
        <div className="summ-word-section">
          <p className="summ-word-section__title">{t('sentenceConstruction.masteredCount', { count: masteredList.length })}</p>
          {masteredList.map((s, i) => (
            <div key={i} className="summ-word-row">
              <span className="summ-word-row__word">{s.original || s.french}</span>
              <span className="summ-word-row__badge summ-word-row__badge--ok">✓</span>
            </div>
          ))}
        </div>
      )}
      {reviewList.length > 0 && (
        <div className="summ-word-section">
          <p className="summ-word-section__title">{t('learnMode.summary.toReviewCount', { count: reviewList.length })}</p>
          {reviewList.map((s, i) => (
            <div key={i} className="summ-word-row">
              <span className="summ-word-row__word">{s.original || s.french}</span>
              <span className="summ-word-row__badge summ-word-row__badge--miss">↺</span>
            </div>
          ))}
        </div>
      )}
    </SummaryScreen>
  );
})()}

        </div>

        {/* ✅ MODAL DE CONFIRMATION DE SORTIE */}
        <ConfirmationModal
          isOpen={showExitModal}
          onConfirm={handleExitConfirm}
          onCancel={handleExitCancel}
          title={t('sentenceConstruction.exitModal.title') as string}
          message={`${t('sentenceConstruction.exitModal.inProgress')}${learnedSentences.length > 0 ? t('sentenceConstruction.exitModal.savedCount', { count: learnedSentences.length }) : t('sentenceConstruction.exitModal.savedGeneric')}${t('sentenceConstruction.exitModal.confirmQuestion')}`}
          confirmText={t('sentenceConstruction.exitModal.confirm') as string}
          cancelText={t('learnMode.exitConfirm.cancel') as string}
          confirmButtonStyle="danger"
        />
      </div>
    </DndProvider>
  );
};

export default SentenceConstructionGame;
